import { useState, useEffect } from "react"
import { isEmpty, uniqBy } from "lodash"
import { useShallow } from "zustand/react/shallow"

// components
import RJOutcomeMeasures from "./RJOutcomeMeasures"
import CellValidation from "../components/CellValidation/CellValidation"
import TableVersionComparisonWrapper from "../../components/TableVersionComparisonWrapper/TableVersionComparisonWrapper"

// context
import useResultsTableArmsStore, {
  initOutcomeArmOptions,
  initAdverseArmOptions,
} from "../../../hooks/stores/useResultsTableArmsStore"
import useAuthoringViewStore from "../../../hooks/stores/useAuthoringViewStore"
import useAuthoringVersionStore from "../../../hooks/stores/useAuthoringVersionStore"
import useParticipantFlowStore from "../RJParticipantFlow/hooks/useParticipantFlowStore"
import useResultsOutcomeStore, { initialState } from "../../../hooks/stores/useResultsOutcomeMeasureStore"

const OutcomeWrapper = (props) => {
  const { formContext, onChange } = props
  const { formDataRef } = formContext

  const { versionData, selectedVersion } = useAuthoringVersionStore(
    useShallow((state) => ({
      versionData: state.versionData,
      selectedVersion: state.selectedVersion,
    }))
  )

  const { formStatus, readOnly, showVersionCompare } = useAuthoringViewStore(
    useShallow((state) => ({
      formStatus: state.formStatus,
      readOnly: state.readOnly,
      showVersionCompare: state.showVersionCompare,
    }))
  )

  const { baselineSas, baselinePeriod, setOutcomeArmOptions, setAdverseArmOptions } = useResultsTableArmsStore()

  const { participantData } = useParticipantFlowStore(
    useShallow((state) => ({
      participantData: state.participantData,
    }))
  )

  const { outcomeData, setOutcomeData, resetResultsOutcomeStore } = useResultsOutcomeStore(
    useShallow((state) => ({
      outcomeData: state.outcomeData,
      setOutcomeData: state.setOutcomeData,
      resetResultsOutcomeStore: state.resetResultsOutcomeStore,
    }))
  )

  const [tableMounting, setTableMounting] = useState(true)

  useEffect(() => {
    if (formStatus === "ready" && outcomeData && readOnly === false && tableMounting !== true) {
      onChange(outcomeData)
    }
  }, [outcomeData])

  useEffect(() => {
    if (formDataRef?.results?.outcome_measures?.outcome_measures_table) {
      if (isEmpty(formDataRef.results.outcome_measures.outcome_measures_table)) {
        setOutcomeData(initialState)
      } else {
        setOutcomeData(formDataRef.results.outcome_measures.outcome_measures_table)
      }
    }
    setTableMounting(false)

    return () => {
      setTableMounting(true)
      resetResultsOutcomeStore()
    }
  }, [])

  useEffect(() => {
    if (participantData) {
      onSetOutcomeArmOptions()
    }
  }, [baselineSas, baselinePeriod])

  const onSetOutcomeArmOptions = () => {
    let items = [...initOutcomeArmOptions]

    if (participantData?.eudract_periods) {
      let periodArms = participantData.eudract_periods.map((period, i) => {
        return {
          id: period.id,
          text: `(EUD) ${period?.baseline_period === "Yes" ? "Baseline" : ""} Period ${i + 1} Arms: ${period.title}`,
        }
      })
      items = [...items, ...periodArms]
      setAdverseArmOptions([...initAdverseArmOptions, ...periodArms])
    }

    if (baselineSas) {
      let sets = baselineSas.map((set) => {
        return {
          id: set.id,
          text: `(EUD SAS) ${set.title ? set.title : '"Arm/Group Description"'} (${
            set.description ? set.description : '"Arm/Group Description'
          })`,
        }
      })
      items = [...items, ...sets]
    }

    setOutcomeArmOptions(uniqBy(items, "id"))
  }

  const omVersionData = versionData?.study_data?.results?.outcome_measures?.outcome_measures_table

  const hasOms = () => {
    const registries = ["ctg", "eudract"]
    for (let i = 0; i < registries.length; i++) {
      const registry = registries[i]
      if (omVersionData?.[registry]?.outcome_measures?.length > 0) return true
    }
    return false
  }

  if (tableMounting) return <></>

  return (
    <div
      className={["form-table-print"].join(" ")}
      style={{ alignSelf: "flex-start" }}
    >
      <RJOutcomeMeasures
        {...props}
        outcomeData={outcomeData}
        setOutcomeData={setOutcomeData}
      />
      {versionData && selectedVersion && showVersionCompare && (
        <TableVersionComparisonWrapper>
          {omVersionData && hasOms() ? (
            <RJOutcomeMeasures
              {...props}
              outcomeData={omVersionData}
              setOutcomeData={() => {}}
            />
          ) : (
            <div>Outcome Measures Not Available</div>
          )}
        </TableVersionComparisonWrapper>
      )}
      <CellValidation
        table="outcome"
        errorKey="results_outcome_measures"
      />
      <CellValidation
        table="outcome"
        errorKey="results_outcome_measures_outcome_measures_table"
      />
    </div>
  )
}

export default OutcomeWrapper
